import { FireTVCampaignAutomationClient } from '@amzn/fire-tv-campaign-automation-service';
import { AWSCredentials } from '@aws-amplify/core/internals/utils';
import { Sha256 } from '@aws-crypto/sha256-js';
import { STSClient, AssumeRoleCommand, Credentials } from '@aws-sdk/client-sts';
import { SignatureV4 } from '@smithy/signature-v4';
import { AuthSession } from 'aws-amplify/auth';
import { decode } from 'cbor-x';
import { logger } from 'src/logger';
import { v4 as uuid } from 'uuid';

/**
 * Properties required to initialize a control plane client
 */
interface ControlPlaneClientProps {
  authSession: AuthSession;
  controlPlaneRegion: string;
  userPoolRegion: string;
  crossAccountRoleArn: string;
  controlPlaneEndpoint: string;
}

/**
 * Result of client creation including the client instance and expiration time
 */
interface ControlPlaneClientResult {
  client: FireTVCampaignAutomationClient;
  expiresAt: Date | undefined;
}

/**
 * Utility functions for client operations
 */
export namespace ControlPlaneClientUtils {
  /**
   * Creates and configures a FireTVCampaignAutomationClient with the necessary credentials and middleware
   * @param props - Client initialization properties
   * @returns Configured FireTVCampaignAutomationClient instance
   * @throws Error if credentials are missing or invalid
   */
  export const getControlPlaneClient = async ({
    authSession,
    controlPlaneRegion,
    userPoolRegion,
    crossAccountRoleArn,
    controlPlaneEndpoint,
  }: ControlPlaneClientProps): Promise<ControlPlaneClientResult> => {
    const sessionCredentials = authSession?.credentials;
    if (!sessionCredentials) {
      throw new Error('No session credentials found');
    }

    const assumeRoleCredentials = await getControlPlaneAssumedCredentials(
      sessionCredentials,
      crossAccountRoleArn,
      userPoolRegion,
    );
    if (!assumeRoleCredentials) {
      throw new Error(`Failed to assume role: ${crossAccountRoleArn}`);
    }

    const credentials = mapCredentials(assumeRoleCredentials);
    validateCredentials(credentials);

    const client = createClient(controlPlaneRegion, credentials, controlPlaneEndpoint);
    configureClientMiddleware(client);

    return {
      client,
      expiresAt: assumeRoleCredentials.Expiration
        ? new Date(assumeRoleCredentials.Expiration)
        : undefined,
    };
  };

  /**
   * Maps AWS credentials to the required format
   * @param assumeRoleCredentials - AWS credentials from assumed role
   * @returns Mapped credentials object
   */
  const mapCredentials = (assumeRoleCredentials: Credentials) => ({
    accessKeyId: assumeRoleCredentials.AccessKeyId || '',
    secretAccessKey: assumeRoleCredentials.SecretAccessKey || '',
    sessionToken: assumeRoleCredentials.SessionToken || '',
  });

  /**
   * Validates that all required credential fields are present
   * @param credentials - Credentials to validate
   * @throws Error if any required credential is missing
   */
  const validateCredentials = (credentials: {
    accessKeyId?: string;
    secretAccessKey?: string;
    sessionToken?: string;
  }) => {
    if (!credentials.accessKeyId || !credentials.secretAccessKey || !credentials.sessionToken) {
      throw new Error('Invalid credentials configuration');
    }
  };

  /**
   * Creates a SignatureV4 signer instance
   * @param region - AWS region
   * @param credentials - AWS credentials
   * @returns SignatureV4 instance
   */
  const createSigner = (
    region: string,
    credentials: {
      accessKeyId: string;
      secretAccessKey: string;
      sessionToken: string;
    },
  ): SignatureV4 =>
    new SignatureV4({
      service: 'execute-api',
      region,
      sha256: Sha256,
      credentials,
      applyChecksum: false,
    });

  /**
   * Creates a configured FireTVCampaignAutomationClient instance
   * @param region - AWS region
   * @param credentials - AWS credentials
   * @returns Configured client instance
   */
  const createClient = (
    region: string,
    credentials: {
      accessKeyId: string;
      secretAccessKey: string;
      sessionToken: string;
    },
    controlPlaneEndpoint: string,
  ): FireTVCampaignAutomationClient =>
    new FireTVCampaignAutomationClient({
      logger,
      region,
      endpoint: controlPlaneEndpoint,
      signer: createSigner(region, credentials),
      credentials,
    });

  /**
   * Configures middleware for the client to handle CBOR responses
   * @param client - Client instance to configure
   *
   * This middleware is a work around since the API gateway response is not recognized as a valid
   * CBOR response and hence the custom deserializer is added to the catch block to handle the
   * response.
   *
   * @see {@link https://t.corp.amazon.com/P157083987 | Open Bug}
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const configureClientMiddleware = (client: FireTVCampaignAutomationClient) => {
    client.middlewareStack.add(
      (next: any) => async (args: any) => {
        try {
          const response = await next(args);
          return response;
        } catch (error: any) {
          if (error.$response?.body instanceof ReadableStream) {
            const reader = error.$response.body.getReader();

            try {
              const { value } = await reader.read();

              if (value instanceof Uint8Array) {
                logger.debug('Attempting CBOR decode', {
                  dataLength: value.length,
                  firstBytes: Array.from(value.slice(0, 10)),
                });

                try {
                  // Decode CBOR data
                  const decodedData = decode(value);

                  logger.debug('CBOR decode successful', {
                    decodedType: typeof decodedData,
                    decodedValue: decodedData,
                  });

                  // Return both response and output properties
                  return {
                    response: error.$response,
                    output: decodedData,
                  };
                } catch (cborError: any) {
                  logger.error('CBOR decode failed', {
                    error: cborError.message,
                  });
                }
              }

              await reader.cancel();
            } catch (streamError: any) {
              logger.error('Stream reading error', {
                error: streamError.message,
              });
            }
          }

          throw error;
        }
      },
      {
        step: 'build',
        name: `cborDeserializer-${Date.now()}`,
        priority: 'high',
      },
    );
  };

  /**
   * Gets assumed role credentials for cross-account access
   * @param credentials - Base AWS credentials
   * @param crossAccountRoleArn - ARN of the role to assume
   * @param region - AWS region
   * @returns Assumed role credentials
   */
  const getControlPlaneAssumedCredentials = async (
    credentials: AWSCredentials,
    crossAccountRoleArn: string,
    region: string,
  ): Promise<Credentials> => {
    const sts = new STSClient({
      credentials,
      region,
    });

    const command = new AssumeRoleCommand({
      RoleArn: crossAccountRoleArn,
      RoleSessionName: uuid(),
    });

    const assumedRole = await sts.send(command);

    if (!assumedRole.Credentials) {
      throw new Error('Failed to get assumed role credentials');
    }

    return assumedRole.Credentials;
  };
}
